// @ts-nocheck

import React, { useCallback, useState } from 'react';
import styled, { css } from 'styled-components';

import { FreebetsIcon } from '../../assets/img/betting';
import Formatter from '../../i18n/formatter';
import { getCurrency, __ } from '../../i18n/l10n';
import theme from '../../styled-components/dark';
import BasketKeyboardContainer from '../BasketKeyboard/BasketKeyboardContainer';
import OverModal from '../OverModal';
import RoundedButton from '../RoundedButton';
import StakeInput from '../StakeInput';

const ReadOnly = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.2em 0.3em;
  border-radius: 5px;
  background-color: ${theme.Basket.readOnlyStakeBackgroundColor};
  margin: 0.4em 0;
  width: 100%;
`;

const Left = styled.span`
  ${({ $highlight }) => {
    return css`
      font-size: ${!$highlight ? '0.9em' : undefined};
      color: ${theme.Basket.readOnlyStakeLeftColor};
    `;
  }}
`;

const StakeValue = styled.div`
  font-weight: bold;

  ${({ $highlight }) => {
    return css`
      font-size: ${$highlight ? '1.1em' : undefined};
      color: ${theme.Basket.readOnlyStakeRightColor};
    `;
  }}
`;

const Modal = styled(OverModal)`
  .modal-content {
    background-color: ${theme.Basket.keyboardMobileModalBackgroundColor};
  }
`;

const StyledRoundedButton = styled(RoundedButton)`
  margin: 10px;
  text-transform: capitalize;
  font-size: 1.2em;
`;

const FreeBetsImg = styled.img`
  height: 1.2em;
  width: 1.2em;
  vertical-align: middle;
  margin-left: 0.5em;
`;

type BasketStakeProps = {
  className?: string;
  isReadOnly: boolean;
  isDisabled: boolean;
  isFreebet: boolean;
  stake: string;
  shouldOpenKeyboardModal: boolean;
  hasError: boolean;
  itemId: number;
  placeholderLabel: string;
  leftSymbol: boolean;
  isInSummary: boolean;
  onStakeChange: (...params: unknown) => unknown;
  basketSetKeyboard?: (...params: unknown) => unknown;
  isFromFreebets?: boolean;
};

const BasketStake = ({
  className,
  isReadOnly,
  isFreebet,
  stake,
  placeholderLabel,
  itemId,
  hasError,
  leftSymbol,
  shouldOpenKeyboardModal,
  isDisabled,
  isInSummary,
  onStakeChange,
  basketSetKeyboard,
  isFromFreebets = false,
}: BasketStakeProps) => {
  const [isStakeFocus, setIsStakeFocus] = useState(false);

  const onFocus = useCallback(() => {
    setIsStakeFocus(true);
    if (basketSetKeyboard) {
      basketSetKeyboard(true);
    }
  }, [setIsStakeFocus, basketSetKeyboard]);

  const renderInput = useCallback(
    () => (
      <StakeInput
        leftSymbol={leftSymbol}
        onStakeChange={onStakeChange}
        placeholderLabel={placeholderLabel}
        stake={stake}
        hasError={hasError}
        onFocus={onFocus}
        isDisabled={isDisabled}
      />
    ),
    [onFocus, leftSymbol, onStakeChange, placeholderLabel, stake, hasError, isDisabled],
  );

  if (isReadOnly) {
    return (
      <ReadOnly className={className}>
        <Left $highlight={isInSummary}>
          {placeholderLabel}
          {isFreebet || isFromFreebets ? <FreeBetsImg src={FreebetsIcon} /> : null}
        </Left>
        <StakeValue $highlight={isInSummary}>{Formatter.formatNumber(parseFloat(stake), getCurrency())}</StakeValue>
      </ReadOnly>
    );
  } else if (shouldOpenKeyboardModal) {
    return (
      <>
        {renderInput()}
        <Modal isOpen={isStakeFocus && !isDisabled} onClose={() => setIsStakeFocus(false)}>
          {renderInput()}
          <BasketKeyboardContainer itemId={itemId} />
          <StyledRoundedButton
            onClick={() => setIsStakeFocus(false)}
            label={__('ok')}
            color={theme.History.betslip.actions.buttonColor}
            backgroundColor={theme.History.betslip.actions.completeButtonBackgroundColor}
          />
        </Modal>
      </>
    );
  }

  return renderInput();
};

export default BasketStake;
